import { AxiosResponse } from 'axios';
import { Module } from 'vuex';
import { SET_DRIVE_BROWSE, SET_DRIVE_MUTATION, SET_DRIVE_TREE, SET_DRIVE_USAGE, SET_ERRORMSG } from '@/store/mutation_types';
import {
  BROWSE_DRIVE,
  CANCEL_DRIVE_UPLOAD,
  CLEAR_DRIVE_ISLOADING,
  COMPLETE_DRIVE_UPLOAD,
  CREATE_DRIVE_FOLDER,
  DELETE_DRIVE_FILE,
  DELETE_DRIVE_FOLDER,
  GET_DRIVE_DOWNLOAD_LINK,
  GET_DRIVE_TREE,
  GET_DRIVE_USAGE,
  INIT_DRIVE_UPLOAD,
  UPDATE_DRIVE_FILE,
  UPDATE_DRIVE_FOLDER,
} from '@/store/action_types';
import Drive from '@/services/drive';
import { BrowseDriveParams, CreateDriveFolder, InitDriveUpload, UpdateDriveFile, UpdateDriveFolder } from '@/dtos/drive.dto';

// Only the latest browse request may write to the store: a slow listing of the folder the
// person already left never replaces the one on screen.
let browseRequest = 0;

/** Runs a write and answers with its data, or false once the API client has shown the reason. */
const mutate = async (commit: any, request: () => Promise<AxiosResponse>): Promise<any> => {
  commit(SET_DRIVE_MUTATION, { isLoading: true });
  try {
    const result = await request();
    if (result && result.data?.status) return result.data.data;
    if (result && result.data?.msg) commit(SET_ERRORMSG, result.data.msg);
    return false;
  } finally {
    commit(SET_DRIVE_MUTATION, { isLoading: false });
  }
};

const drive: Module<any, {}> = {
  state: {
    driveTree: { data: null, isLoading: false },
    driveBrowse: { data: null, isLoading: false },
    driveUsage: { data: null, isLoading: false },
    driveMutation: { isLoading: false },
  },
  getters: {
    driveTree: (state: any) => state.driveTree,
    driveBrowse: (state: any) => state.driveBrowse,
    driveUsage: (state: any) => state.driveUsage,
    driveMutation: (state: any) => state.driveMutation,
  },
  mutations: {
    [SET_DRIVE_TREE]: (state: any, payload: { data?: any; isLoading: boolean }) => {
      if (payload.data !== undefined) state.driveTree.data = payload.data;
      state.driveTree.isLoading = payload.isLoading;
    },
    [SET_DRIVE_BROWSE]: (state: any, payload: { data?: any; isLoading: boolean }) => {
      if (payload.data !== undefined) state.driveBrowse.data = payload.data;
      state.driveBrowse.isLoading = payload.isLoading;
    },
    [SET_DRIVE_USAGE]: (state: any, payload: { data?: any; isLoading: boolean }) => {
      if (payload.data !== undefined) state.driveUsage.data = payload.data;
      state.driveUsage.isLoading = payload.isLoading;
    },
    [SET_DRIVE_MUTATION]: (state: any, payload: { isLoading: boolean }) => {
      state.driveMutation.isLoading = payload.isLoading;
    },
  },
  actions: {
    [GET_DRIVE_TREE]: async ({ commit }: any) => {
      commit(SET_DRIVE_TREE, { isLoading: true });
      const result: AxiosResponse = await Drive.getTree();
      if (result && result.data?.status) {
        commit(SET_DRIVE_TREE, { data: result.data.data, isLoading: false });
        if (result.data.data?.usage) commit(SET_DRIVE_USAGE, { data: result.data.data.usage, isLoading: false });
        return result.data.data;
      }
      commit(SET_DRIVE_TREE, { isLoading: false });
      if (result && result.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [BROWSE_DRIVE]: async ({ commit }: any, payload: BrowseDriveParams) => {
      const request = ++browseRequest;
      commit(SET_DRIVE_BROWSE, { isLoading: true });
      const result: AxiosResponse = await Drive.browse(payload);
      // overtaken by a newer listing: neither a success nor a failure for the caller
      if (request !== browseRequest) return null;
      if (result && result.data?.status) {
        commit(SET_DRIVE_BROWSE, { data: result.data.data, isLoading: false });
        if (result.data.data?.usage) commit(SET_DRIVE_USAGE, { data: result.data.data.usage, isLoading: false });
        return result.data.data;
      }
      commit(SET_DRIVE_BROWSE, { isLoading: false });
      if (result && result.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [GET_DRIVE_USAGE]: async ({ commit }: any) => {
      commit(SET_DRIVE_USAGE, { isLoading: true });
      const result: AxiosResponse = await Drive.getUsage();
      if (result && result.data?.status) {
        commit(SET_DRIVE_USAGE, { data: result.data.data, isLoading: false });
        return result.data.data;
      }
      commit(SET_DRIVE_USAGE, { isLoading: false });
      return false;
    },
    [CREATE_DRIVE_FOLDER]: ({ commit }: any, payload: CreateDriveFolder) => mutate(commit, () => Drive.createFolder(payload)),
    [UPDATE_DRIVE_FOLDER]: ({ commit }: any, payload: UpdateDriveFolder) => mutate(commit, () => Drive.updateFolder(payload)),
    [DELETE_DRIVE_FOLDER]: ({ commit }: any, id: string) => mutate(commit, () => Drive.deleteFolder(id)),
    [UPDATE_DRIVE_FILE]: ({ commit }: any, payload: UpdateDriveFile) => mutate(commit, () => Drive.updateFile(payload)),
    [DELETE_DRIVE_FILE]: ({ commit }: any, id: string) => mutate(commit, () => Drive.deleteFile(id)),
    // Uploads run several at a time, so they keep their own progress state and never touch the mutation flag.
    [INIT_DRIVE_UPLOAD]: async ({ commit }: any, payload: InitDriveUpload) => {
      const result: AxiosResponse = await Drive.initUpload(payload);
      if (result && result.data?.status) return result.data.data;
      if (result && result.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [COMPLETE_DRIVE_UPLOAD]: async ({ commit }: any, id: string) => {
      const result: AxiosResponse = await Drive.completeUpload(id);
      if (result && result.data?.status) return result.data.data;
      if (result && result.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [CANCEL_DRIVE_UPLOAD]: async (_context: any, id: string) => {
      const result: AxiosResponse = await Drive.cancelUpload(id).catch(() => null as any);
      return Boolean(result && result.data?.status);
    },
    [GET_DRIVE_DOWNLOAD_LINK]: async ({ commit }: any, payload: { id: string; inline: boolean }) => {
      const result: AxiosResponse = await Drive.downloadLink(payload.id, payload.inline);
      if (result && result.data?.status) return result.data.data;
      if (result && result.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [CLEAR_DRIVE_ISLOADING]: ({ commit }: any) => {
      commit(SET_DRIVE_TREE, { isLoading: false });
      commit(SET_DRIVE_BROWSE, { isLoading: false });
      commit(SET_DRIVE_USAGE, { isLoading: false });
      commit(SET_DRIVE_MUTATION, { isLoading: false });
    },
  },
};

export default drive;
